// Suppliers (M6): who you buy from, searchable, with their VAT status, lead time and how many
// items you buy from them; imported ones marked. Their contact details show only on each
// supplier's own page. "n" adds a supplier (UI6A-110); the loading state is the table's own
// columns (UI6A-20).

import type { SupplierSummary } from '@enp/domain/api';
import {
  Badge,
  Button,
  cn,
  createColumnHelper,
  DataTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Switch,
  toast,
  type ColumnDef,
} from '@enp/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Archive, ChevronRight, Plus, Search, SearchX, Truck, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { newId } from '@enp/domain/ids';
import {
  KeyHints,
  ProvenanceBadges,
  PurchasingViews,
  TableSkeleton,
  useCan,
  usePageKeys,
} from '../../../../purchasing/parts.js';
import {
  createSupplier,
  invalidatePurchasing,
  type SuppliersSearch,
  suppliersQuery,
  suppliersSearchSchema,
} from '../../../../purchasing/queries.js';
import { daysText, vatSwitchHelp } from '../../../../purchasing/words.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { usePageSearch } from '../../../../shell/signals.js';
import { Notice } from '../../../../stations/page-parts.js';
import { LoadMoreFooter } from '../../../../stock/components.js';
import { clean } from '../../../../stock/queries.js';
import { actionProblem } from '../../../../stock-control/parts.js';

export const Route = createFileRoute('/_app/purchasing/suppliers/')({
  validateSearch: suppliersSearchSchema,
  component: SuppliersPage,
});

const col = createColumnHelper<SupplierSummary>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<SupplierSummary, any>[] = [
  col.display({
    id: 'name',
    header: 'Supplier',
    meta: { width: 'min-w-52' },
    cell: ({ row }) => (
      <span className="flex flex-col leading-tight">
        <Link
          to="/purchasing/suppliers/$supplierId"
          params={{ supplierId: row.original.id }}
          tabIndex={-1}
          className="font-medium text-fg hover:underline"
        >
          {row.original.name}
        </Link>
        <span className="text-xs text-muted">{row.original.code ?? 'No code'}</span>
      </span>
    ),
  }),
  col.display({
    id: 'vat',
    header: 'VAT',
    cell: ({ row }) =>
      row.original.vat_registered ? (
        <span className="text-sm text-fg">Registered</span>
      ) : (
        <Badge tone="neutral">Not registered: no VAT</Badge>
      ),
  }),
  col.display({
    id: 'lead',
    header: 'Lead time',
    cell: ({ row }) =>
      row.original.lead_time_days === null ? (
        <span className="text-sm text-muted">Not set</span>
      ) : (
        <span className="text-sm text-fg">{daysText(row.original.lead_time_days)}</span>
      ),
  }),
  col.display({
    id: 'items',
    header: 'Items',
    meta: { align: 'right' },
    cell: ({ row }) => <span className="tabular-nums text-fg">{row.original.item_count}</span>,
  }),
  col.display({
    id: 'source',
    header: 'Where from',
    meta: { className: 'hidden lg:table-cell' },
    cell: ({ row }) =>
      row.original.archived_at ? (
        <Badge tone="neutral">
          <Archive aria-hidden />
          Archived
        </Badge>
      ) : (
        <ProvenanceBadges source={row.original.source} personOwned={[]} />
      ),
  }),
  col.display({
    id: 'open',
    header: () => <span className="sr-only">Open</span>,
    meta: { width: 'w-8' },
    cell: () => <ChevronRight aria-hidden className="size-4 text-muted" />,
  }),
];

/** Adds a supplier: its name, VAT status and lead time; the rest on its page. */
function AddSupplierDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [vat, setVat] = useState(true);
  const [lead, setLead] = useState('');
  const [errors, setErrors] = useState<{ name?: string; lead?: string }>({});
  // One key per form: a retried save answers the first supplier (D-063).
  const attempt = useRef(newId());
  const create = useMutation({
    mutationFn: (body: Parameters<typeof createSupplier>[0]) =>
      createSupplier(body, attempt.current),
    onSuccess: async (s) => {
      await invalidatePurchasing(queryClient);
      toast.success(`${s.name} added`, { description: 'Add what you buy from them next.' });
      onClose();
      void navigate({ to: '/purchasing/suppliers/$supplierId', params: { supplierId: s.id } });
    },
  });
  const failed = create.isError ? actionProblem(create.error, 'The supplier wasn’t added') : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Give the supplier a name.';
    const days = lead.trim() === '' ? null : Number(lead);
    if (days !== null && (!Number.isInteger(days) || days < 0 || days > 365)) {
      next.lead = 'Whole days, 0 to 365.';
    }
    setErrors(next);
    if (next.name || next.lead) return;
    create.mutate({
      name: name.trim(),
      vat_registered: vat,
      ...(days === null ? {} : { lead_time_days: days }),
    });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogForm onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Add a supplier</DialogTitle>
            <DialogDescription>
              Their contact details, what you buy from them and their prices go on the supplier’s
              page next.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <FormField label="Name" required error={errors.name}>
              <Input
                autoFocus
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
              />
            </FormField>
            <FormField label="VAT-registered" inline description={vatSwitchHelp(vat)}>
              {(control) => <Switch {...control} checked={vat} onCheckedChange={setVat} />}
            </FormField>
            <FormField
              label="Lead time (days)"
              error={errors.lead}
              description="Days from ordering to delivery. The restock suggestion covers it."
            >
              <Input
                inputMode="numeric"
                value={lead}
                onChange={(e) => setLead(e.target.value)}
                className="w-28"
              />
            </FormField>
            {failed && (
              <Notice tone="danger" alert title={failed.title} technical={failed.technical}>
                {failed.explain}
              </Notice>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Add supplier
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function SuppliersPage() {
  useDocumentTitle('Suppliers');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canEdit = useCan('purchasing.order', true);
  const [adding, setAdding] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  usePageSearch(searchRef);
  const setSearch = (patch: Partial<SuppliersSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });
  usePageKeys({ n: canEdit ? () => setAdding(true) : undefined });

  // Types freely; the URL (and the server search) follows after a pause.
  const [text, setText] = useState(search.q ?? '');
  useEffect(() => {
    const q = text.trim();
    if (q === (search.q ?? '')) return;
    const timer = setTimeout(() => setSearch({ q: q || undefined }), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const list = useInfiniteQuery(suppliersQuery(search));
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const total = list.data?.pages[0]?.total;
  const filtered = Boolean(search.q || search.archived);
  // No supplier at all: the empty state's "Add a supplier" is the one action (UI8C-6).
  const noneYet = !filtered && list.data !== undefined && items.length === 0;

  const empty = filtered ? (
    <EmptyState
      compact
      icon={SearchX}
      title={search.archived && !search.q ? 'No archived suppliers' : 'No suppliers match'}
      description={
        search.q
          ? `No supplier’s name or code contains “${search.q}”.`
          : 'Archived suppliers show here.'
      }
      action={
        <Button
          variant="secondary"
          icon={<X aria-hidden />}
          onClick={() => {
            setText('');
            void navigate({ search: {} });
          }}
        >
          Show live suppliers
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={Truck}
      title="No suppliers yet"
      description="Suppliers come in with a catalogue import, or you add them here. Then add what you buy from each, and their prices."
      action={
        canEdit ? (
          <Button icon={<Plus aria-hidden />} onClick={() => setAdding(true)}>
            Add a supplier
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <>
      <PageHeader
        title="Purchasing"
        description="Who you buy from, what you buy from each and at what price."
        actions={
          canEdit && !noneYet ? (
            <Button
              icon={<Plus aria-hidden />}
              aria-keyshortcuts="n"
              onClick={() => setAdding(true)}
            >
              Add a supplier
            </Button>
          ) : undefined
        }
      />
      <PurchasingViews current="suppliers" />
      <div
        className="mb-3 flex flex-wrap items-center gap-2"
        role="search"
        aria-label="Supplier search"
      >
        <div className="relative w-full max-w-64">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
          />
          <Input
            ref={searchRef}
            type="search"
            aria-label="Search suppliers"
            aria-keyshortcuts="/"
            placeholder="Search by name or code"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="pl-8"
          />
        </div>
        <Button
          variant="secondary"
          aria-pressed={Boolean(search.archived)}
          className={cn(search.archived && 'border-primary/60 bg-primary-subtle text-primary-text')}
          icon={<Archive aria-hidden />}
          onClick={() => setSearch({ archived: search.archived ? undefined : true })}
        >
          Archived
        </Button>
        <span className="ml-auto">
          <KeyHints
            keys={[['/', 'search'], ...(canEdit ? ([['n', 'add a supplier']] as const) : [])]}
          />
        </span>
      </div>
      {list.isError && !list.data ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The suppliers"
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <TableSkeleton
          label="Suppliers"
          columns={[
            { header: 'Supplier', className: 'min-w-52', twoLines: true },
            { header: 'VAT' },
            { header: 'Lead time' },
            { header: 'Items', align: 'right' },
            { header: 'Where from', className: 'hidden lg:table-cell' },
            { header: '', className: 'w-8', empty: true },
          ]}
        />
      ) : (
        <DataTable
          label="Suppliers"
          columns={columns}
          data={items}
          loading={false}
          empty={empty}
          filterable={false}
          getRowId={(s) => s.id}
          onRowActivate={(s) =>
            void navigate({ to: '/purchasing/suppliers/$supplierId', params: { supplierId: s.id } })
          }
          footer={
            items.length > 0 ? (
              <LoadMoreFooter
                loaded={items.length}
                total={total}
                noun={['supplier', 'suppliers']}
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                updating={list.isFetching && !list.isFetchingNextPage}
                failed={list.isError && Boolean(list.data)}
                onMore={() => void list.fetchNextPage()}
              />
            ) : undefined
          }
          className="xl:max-h-[calc(100dvh-22rem)]"
        />
      )}
      {adding && <AddSupplierDialog onClose={() => setAdding(false)} />}
    </>
  );
}
